import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { NgTemplateOutlet } from '@angular/common';
import { WIDGET_COLOR_PARAMS, WidgetColorParam } from 'src/app/services/platform/widget.service';
import { DEFAULT_MAP_TINT, Rgba, mixColors, parseColor, tintColor, toCssColor } from 'src/app/services/map/map-palette.service';
import { HttpService } from 'src/app/services/core/http.service';
import { ModalOptionPanelComponent } from '../modals/modal-option-panel/modal-option-panel.component';
import {
  ACCESS_PARAMS, CHARGER_TYPES, COLOR_PARAMS, DocParam, MAP_PARAMS, PRESETS, SETTINGS_PARAMS, UI_SWITCH_PARAMS,
  VEHICLE_PARAMS, WidgetParamValues, WidgetPreset, buildWidgetUrl, encodeWidgetValue, parseWidgetUrl, widgetUrlParams,
} from './widget-doc-params';
import { PLATFORMS, WidgetPlatform, bridgeSnippets, setupSnippets } from './widget-doc-snippets';

/** Route path of this page — kept next to the component like WIDGET_PATH is. */
export const WIDGET_DOCUMENTATION_PATH = 'widget-documentation';

export interface WidgetDocSection {
  id: string;
  title: string;
}

interface VehicleOption {
  id: number;
  label: string;
}

interface OperatorOption {
  id: number;
  name: string;
}

/** localStorage keys — per-viewer conveniences only; the API key is deliberately not stored. */
const SKIN_STORAGE_KEY = 'widgetDocumentationSkin';
const PLATFORM_STORAGE_KEY = 'widgetDocumentationPlatform';
const CONFIG_STORAGE_KEY = 'widgetDocumentationConfig';

/** The preview's fake map, per skin — stands in for the evlight / evdark basemap. */
const PREVIEW_MAP = {
  light: { bg: '#EEF1F5', road: '#C9CFD8', roadMinor: '#DDE1E7' },
  dark: { bg: '#1B2430', road: '#33404F', roadMinor: '#26313E' },
};

const WHITE: Rgba = { r: 255, g: 255, b: 255, a: 1 };

/** `<input type="color">` only takes `#rrggbb`. */
function toHex6(value: string): string {
  const color = parseColor(value);
  if (!color) return '#000000';
  return '#' + [color.r, color.g, color.b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('');
}

/** Decimals a number needs to be written at the given step (0.1 → 1, 0.0001 → 4). */
function stepDecimals(step: number): number {
  const text = String(step);
  return text.includes('.') ? text.split('.')[1].length : 0;
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch { }
}

/**
 * Developer documentation of the widget mode, served at `widget.<domain>/widget-documentation`
 * (widget host only, see app.routes.ts) as a standalone page (no map, no resolvers).
 *
 * The page doubles as a URL builder: every documented parameter has a control, and the
 * resulting widget URL stays visible in the sticky bar at the top — ready to open in a browser
 * for testing before it goes into a native app. The platform selector switches the setup and
 * native-bridge code between Android, iOS and Flutter.
 *
 * The content mirrors `docs/widget-mode.md` — when a widget parameter or the JS API changes,
 * update widget-doc-params.ts / widget-doc-snippets.ts and that document.
 *
 * The page is developer-facing and intentionally English-only: its strings are not part of
 * the translation CSV.
 */
@Component({
  standalone: true,
  selector: 'app-widget-documentation',
  templateUrl: './widget-documentation.component.html',
  styleUrls: ['./widget-documentation.component.scss'],
  imports: [ModalOptionPanelComponent, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WidgetDocumentationComponent {

  private readonly httpService = inject(HttpService);
  private readonly destroyRef = inject(DestroyRef);

  /** Id of the element whose "Copy" button was pressed last; clears itself after 2 s. */
  public readonly copiedId = signal<string | null>(null);

  /** The page's own theme; independent of the app's skin setting. */
  public readonly skin = signal<'light' | 'dark'>('light');

  // ---------------------------------------------------------------------------
  // Configuration → URL
  // ---------------------------------------------------------------------------

  /** Every parameter that is set, by name. A parameter that is not in here is not in the URL. */
  public readonly values = signal<WidgetParamValues>(this.storedConfig());

  public readonly url = computed(() => buildWidgetUrl(this.widgetBase(), this.values()));
  public readonly urlParams = computed(() => widgetUrlParams(this.values())
    .map(({ name, value }) => ({ name, value: encodeWidgetValue(value) })));
  public readonly hasApiKey = computed(() => !!this.values()['api_key']);
  /** Parameters set besides api_key. */
  public readonly setCount = computed(() => Object.keys(this.values()).filter((name) => name !== 'api_key').length);

  public readonly importOpen = signal(false);
  public readonly importError = signal(false);

  // ---------------------------------------------------------------------------
  // Platform
  // ---------------------------------------------------------------------------

  public readonly platforms = PLATFORMS;
  public readonly platform = signal<WidgetPlatform>(this.storedPlatform());
  public readonly platformInfo = computed(() => PLATFORMS.find((p) => p.id === this.platform())!);
  public readonly setupCode = computed(() => setupSnippets(this.platform(), this.url()));
  public readonly bridgeCode = computed(() => bridgeSnippets(this.platform()));

  // ---------------------------------------------------------------------------
  // Parameter groups
  // ---------------------------------------------------------------------------

  public readonly accessParams = ACCESS_PARAMS;
  public readonly uiSwitchParams = UI_SWITCH_PARAMS;
  public readonly vehicleParams = VEHICLE_PARAMS;
  public readonly settingsParams = SETTINGS_PARAMS;
  public readonly mapParams = MAP_PARAMS;
  public readonly maptintParam = COLOR_PARAMS.find((param) => param.name === 'maptint')!;
  public readonly chargerTypes = CHARGER_TYPES;
  public readonly presets = PRESETS;

  public readonly sections: WidgetDocSection[] = [
    { id: 'integration', title: 'Integration' },
    { id: 'api-key', title: 'API key' },
    { id: 'presets', title: 'Presets' },
    { id: 'rules', title: 'Parameter rules' },
    { id: 'ui-switches', title: 'UI switches' },
    { id: 'car-params', title: 'Vehicle' },
    { id: 'settings-params', title: 'Application settings' },
    { id: 'colors', title: 'Colors' },
    { id: 'map-params', title: 'Map and mode' },
    { id: 'native-bridge', title: 'Native bridge' },
  ];

  public readonly gpsFields: { field: string, unit: string, required: string, fallback: string }[] = [
    { field: 'lat', unit: 'degrees, −90…90', required: 'yes', fallback: '—' },
    { field: 'lng (or lon)', unit: 'degrees, −180…180', required: 'yes', fallback: '—' },
    { field: 'speed', unit: 'm/s', required: 'no', fallback: '0' },
    { field: 'heading', unit: 'degrees', required: 'no', fallback: '0' },
    { field: 'accuracy', unit: 'metres', required: 'no', fallback: '0' },
    { field: 'altitude', unit: 'metres', required: 'no', fallback: '0' },
    { field: 'timestamp', unit: 'ms since epoch', required: 'no', fallback: 'Date.now()' },
  ];

  // ---------------------------------------------------------------------------
  // Vehicles (carid picker)
  // ---------------------------------------------------------------------------

  /** Brand → models, from the public vehicle list; empty while loading or when it failed. */
  public readonly vehicleGroups = signal<{ brand: string, vehicles: VehicleOption[] }[]>([]);
  public readonly vehicleNames = computed(() => {
    const names = new Map<number, string>();
    this.vehicleGroups().forEach((group) => group.vehicles.forEach((v) => names.set(v.id, v.label)));
    return names;
  });
  public readonly selectedVehicleName = computed(() => {
    const id = Number(this.values()['carid']);
    return Number.isFinite(id) ? this.vehicleNames().get(id) ?? null : null;
  });

  // ---------------------------------------------------------------------------
  // Charging operators (favoriteoperators picker)
  // ---------------------------------------------------------------------------

  /** Every operator by name, from the public operator list; empty while loading or when it failed. */
  public readonly operators = signal<OperatorOption[]>([]);
  private readonly operatorNames = computed(() => new Map(this.operators().map((op) => [op.id, op.name])));
  /** The ids in `favoriteoperators`, in URL order, with their names once the list is loaded. */
  public readonly selectedOperators = computed(() => this.operatorIds()
    .map((id) => ({ id, name: this.operatorNames().get(id) ?? null })));
  /** Operators the picker still offers. */
  public readonly availableOperators = computed(() => {
    const selected = new Set(this.operatorIds());
    return this.operators().filter((op) => !selected.has(op.id));
  });

  // ---------------------------------------------------------------------------
  // Colors
  // ---------------------------------------------------------------------------

  public readonly colorParams: WidgetColorParam[] = WIDGET_COLOR_PARAMS;
  public readonly defaultMapTint = DEFAULT_MAP_TINT;

  /** `#rrggbb` of every color parameter in the current page theme, before any override. */
  private readonly colorDefaults = signal<Record<string, string>>({});

  /** Colors set in the URL, as CSS colors by parameter name. */
  public readonly colorOverrides = computed(() => {
    const values = this.values();
    const overrides: Record<string, string> = {};
    this.colorParams.forEach((param) => {
      const value = values[param.name];
      if (!value) return;
      const css = /^([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value) ? '#' + value : value;
      if (parseColor(css)) overrides[param.name] = css;
    });
    return overrides;
  });
  /** `#rrggbb` per color parameter for the pickers: the override, else the theme's value. */
  public readonly colorValues = computed(() => {
    const result: Record<string, string> = { ...this.colorDefaults() };
    Object.entries(this.colorOverrides()).forEach(([name, css]) => result[name] = toHex6(css));
    return result;
  });
  public readonly hasColorOverrides = computed(() => Object.keys(this.colorOverrides()).length > 0);
  public readonly hasColorChanges = computed(() => this.hasColorOverrides() || this.isSet('maptint'));

  /** Same rule as MapPaletteService: an explicit maptint wins, otherwise 25 % once any color is set. */
  public readonly mapTintStrength = computed(() => {
    const tint = this.values()['maptint'];
    if (tint != null) return Math.min(100, Math.max(0, Number(tint))) / 100;
    return this.hasColorOverrides() ? DEFAULT_MAP_TINT / 100 : 0;
  });

  /**
   * Colors set in the URL as custom properties, bound on the preview only — the rest of the page
   * keeps its own palette. The preview re-declares the gradients built from the accent colors.
   */
  public readonly previewStyle = computed(() => {
    const overrides = this.colorOverrides();
    const style: Record<string, string> = {};
    this.colorParams.forEach((param) => {
      const value = overrides[param.name];
      if (value != null) param.tokens.forEach((token) => style[token] = value);
    });
    return { ...style, ...this.previewMapStyle() };
  });

  /**
   * The map side of the preview, computed like the widget does it: the fake basemap tinted
   * toward the palette, and range + cursor in the accent / light text colors when those are set.
   */
  private readonly previewMapStyle = computed(() => {
    const strength = this.mapTintStrength();
    const overrides = this.colorOverrides();
    const values = this.colorValues();
    const style: Record<string, string> = {};
    if (strength <= 0) return style;

    const base = PREVIEW_MAP[this.skin()];
    const ground = parseColor(values['color_background'] ?? '');
    const accent = parseColor(values['color_accent1'] ?? '');
    const tint = (color: string, target: Rgba | null) =>
      target ? toCssColor(tintColor(parseColor(color)!, target, strength)) : color;
    style['--preview-map-bg'] = tint(base.bg, ground);
    style['--preview-map-road'] = tint(base.road, accent);
    style['--preview-map-road-minor'] = tint(base.roadMinor, accent);

    if (accent && (overrides['color_accent1'] || overrides['color_accent2'])) {
      style['--preview-range-color'] = toCssColor(accent);
      style['--preview-range-inner-color'] = toCssColor(mixColors(accent, WHITE, 0.25));
      style['--preview-cursor-arrow'] = toCssColor(accent);
    }
    if (overrides['color_lighttext']) {
      style['--preview-cursor-disc'] = overrides['color_lighttext'];
    }
    return style;
  });

  constructor() {
    // Standalone page: the index.html loading screen is never removed by MainComponent here.
    document.getElementById('loading-screen')?.remove();
    // Light unless this page was last read in dark; the app's own skin setting is never read
    // or written here, so toggling the documentation cannot change the map.
    this.applySkin(readStorage(SKIN_STORAGE_KEY) === 'dark' ? 'dark' : 'light');
    this.loadVehicles();
    this.loadOperators();
  }

  // ---------------------------------------------------------------------------
  // Parameter state
  // ---------------------------------------------------------------------------

  public isSet(name: string): boolean {
    return this.values()[name] != null;
  }

  public value(name: string): string {
    return this.values()[name] ?? '';
  }

  public setValue(name: string, value: string | null): void {
    this.values.update((values) => {
      const next = { ...values };
      if (value == null || value === '') delete next[name];
      else next[name] = value;
      return next;
    });
    this.storeConfig();
  }

  public clear(name: string): void {
    this.setValue(name, null);
  }

  /** Clears every parameter except the API key. */
  public resetAll(): void {
    const apiKey = this.values()['api_key'];
    this.values.set(apiKey ? { api_key: apiKey } : {});
    this.storeConfig();
  }

  public applyPreset(preset: WidgetPreset): void {
    const apiKey = this.values()['api_key'];
    this.values.set({ ...(apiKey ? { api_key: apiKey } : {}), ...preset.values });
    this.storeConfig();
  }

  public isPresetActive(preset: WidgetPreset): boolean {
    const values = this.values();
    const names = Object.keys(values).filter((name) => name !== 'api_key');
    const presetNames = Object.keys(preset.values);
    return names.length === presetNames.length && presetNames.every((name) => values[name] === preset.values[name]);
  }

  /** Paste box of the URL bar: replaces the configuration with the parameters of a widget URL. */
  public importUrl(text: string): void {
    const parsed = parseWidgetUrl(text);
    if (!parsed) {
      this.importError.set(true);
      return;
    }
    const apiKey = parsed['api_key'] ?? this.values()['api_key'];
    this.values.set({ ...parsed, ...(apiKey ? { api_key: apiKey } : {}) });
    this.storeConfig();
    this.importError.set(false);
    this.importOpen.set(false);
  }

  public toggleImport(): void {
    this.importOpen.update((open) => !open);
    this.importError.set(false);
  }

  // ---------------------------------------------------------------------------
  // Control handlers
  // ---------------------------------------------------------------------------

  public rangeValue(param: DocParam): number {
    const control = param.control;
    if (control.kind !== 'range') return 0;
    const value = Number(this.values()[param.name]);
    return this.isSet(param.name) && Number.isFinite(value) ? value : control.start;
  }

  /** Position of the value on the track, 0..100 — drives the gradient fill of the slider. */
  public rangePercent(param: DocParam): number {
    const control = param.control;
    if (control.kind !== 'range' || control.max === control.min) return 0;
    return (this.rangeValue(param) - control.min) / (control.max - control.min) * 100;
  }

  public rangeLabel(param: DocParam): string {
    const control = param.control;
    if (control.kind !== 'range') return '';
    const text = this.rangeValue(param).toFixed(stepDecimals(control.step));
    return control.unit ? `${text} ${control.unit}` : text;
  }

  public onRange(param: DocParam, event: Event): void {
    const control = param.control;
    if (control.kind !== 'range') return;
    const value = Number((event.target as HTMLInputElement).value);
    this.setValue(param.name, value.toFixed(stepDecimals(control.step)));
  }

  /** Number boxes: empty clears, out-of-range values are clamped like the widget does. */
  public onNumber(param: DocParam, event: Event): void {
    const control = param.control;
    const input = event.target as HTMLInputElement;
    if (control.kind !== 'number') return;
    if (input.value.trim() === '') {
      this.clear(param.name);
      return;
    }
    let value = Number(input.value);
    if (!Number.isFinite(value)) return;
    if (control.min != null) value = Math.max(control.min, value);
    if (control.max != null) value = Math.min(control.max, value);
    const text = String(control.step === 1 ? Math.round(value) : value);
    this.setValue(param.name, text);
    if (input.value !== text) input.value = text;
  }

  public onText(name: string, event: Event): void {
    this.setValue(name, (event.target as HTMLInputElement).value.trim());
  }

  public onSelect(name: string, event: Event): void {
    this.setValue(name, (event.target as HTMLSelectElement).value);
  }

  public isCharger(type: string): boolean {
    return this.chargerList().includes(type);
  }

  public toggleCharger(type: string): void {
    const list = this.chargerList();
    const next = list.includes(type) ? list.filter((t) => t !== type) : [...list, type];
    // Written in the documented order, so the URL does not depend on the click order.
    this.setValue('chargers', this.chargerTypes.map((t) => t.value).filter((t) => next.includes(t)).join(','));
  }

  private chargerList(): string[] {
    return (this.values()['chargers'] ?? '').toLowerCase().split(/[,;|+ ]+/)
      .map((name) => name === 'supercharger' ? 'tesla' : name).filter(Boolean);
  }

  /** Picker of `favoriteoperators`: appends the chosen operator and resets the select. */
  public addOperator(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const id = Number(select.value);
    select.value = '';
    if (!Number.isInteger(id) || id <= 0) return;
    this.setOperatorIds([...this.operatorIds(), id]);
  }

  public removeOperator(id: number): void {
    this.setOperatorIds(this.operatorIds().filter((current) => current !== id));
  }

  /** Same parsing as WidgetService.getFavoriteOperatorIds: digits only, duplicates dropped. */
  private operatorIds(): number[] {
    const ids = (this.values()['favoriteoperators'] ?? '').split(/[,;|+ ]+/)
      .filter((part) => /^\d+$/.test(part)).map(Number);
    return [...new Set(ids)];
  }

  private setOperatorIds(ids: number[]): void {
    this.setValue('favoriteoperators', ids.join(','));
  }

  public setColor(name: string, event: Event): void {
    this.setValue(name, (event.target as HTMLInputElement).value.replace('#', '').toUpperCase());
  }

  /** Dark text on light swatches, light text on dark ones (relative luminance). */
  public isLightColor(css: string): boolean {
    const color = parseColor(css);
    if (!color) return true;
    const lin = (c: number) => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
    return 0.2126 * lin(color.r) + 0.7152 * lin(color.g) + 0.0722 * lin(color.b) > 0.4;
  }

  public isColorOverridden(name: string): boolean {
    return this.colorOverrides()[name] != null;
  }

  public resetColors(): void {
    this.values.update((values) => Object.fromEntries(Object.entries(values)
      .filter(([name]) => !name.startsWith('color_') && name !== 'maptint')));
    this.storeConfig();
  }

  // ---------------------------------------------------------------------------
  // Platform / theme
  // ---------------------------------------------------------------------------

  public setPlatform(platform: WidgetPlatform): void {
    this.platform.set(platform);
    writeStorage(PLATFORM_STORAGE_KEY, platform);
  }

  /** Light / Dark buttons of the top bar. */
  public setSkin(skin: 'light' | 'dark'): void {
    if (skin !== this.skin()) this.applySkin(skin);
  }

  private applySkin(skin: 'light' | 'dark'): void {
    this.skin.set(skin);
    // colors.scss keys the light palette off body.lightSkin, so the page follows the same
    // tokens as the modals it is styled after.
    document.body.classList.toggle('lightSkin', skin === 'light');
    this.readColorDefaults();
    writeStorage(SKIN_STORAGE_KEY, skin);
  }

  /** Reads the current skin's token values from the body. */
  private readColorDefaults(): void {
    const style = getComputedStyle(document.body);
    const defaults: Record<string, string> = {};
    this.colorParams.forEach((param) => {
      defaults[param.name] = toHex6(style.getPropertyValue(param.tokens[0]));
    });
    this.colorDefaults.set(defaults);
  }

  // ---------------------------------------------------------------------------
  // Storage / loading
  // ---------------------------------------------------------------------------

  private storedPlatform(): WidgetPlatform {
    const stored = readStorage(PLATFORM_STORAGE_KEY);
    return PLATFORMS.some((p) => p.id === stored) ? stored as WidgetPlatform : 'android';
  }

  private storedConfig(): WidgetParamValues {
    try {
      const parsed = JSON.parse(readStorage(CONFIG_STORAGE_KEY) ?? '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /** Everything but the API key, so a shared machine does not keep it around. */
  private storeConfig(): void {
    const { api_key: _apiKey, ...rest } = this.values();
    writeStorage(CONFIG_STORAGE_KEY, JSON.stringify(rest));
  }

  private loadVehicles(): void {
    this.httpService.getAllVehicles().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        const list = Array.isArray(response) ? response : [];
        const brands = new Map<string, VehicleOption[]>();
        list.filter((v) => v?.is_active == 1 && v.id != null).forEach((v) => {
          const type = String(v.type ?? '').trim();
          // The type is the lower-case brand key ("aiways"); names already start with the brand.
          const brand = type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Other';
          // The name is the full model ("Tesla Model 3 Standard Range RWD (2019)"); the subtype only
          // repeats the model family.
          const label = String(v.name ?? '').trim();
          if (!brands.has(brand)) brands.set(brand, []);
          brands.get(brand)!.push({ id: Number(v.id), label: label || `#${v.id}` });
        });
        this.vehicleGroups.set([...brands.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([brand, vehicles]) => {
            vehicles.sort((a, b) => a.label.localeCompare(b.label));
            return { brand, vehicles };
          }));
      },
      // No list (offline, blocked): the plain id box still works.
      error: () => this.vehicleGroups.set([]),
    });
  }

  /** The operator list is versioned with the charging station data, like in the app's init resolver. */
  private loadOperators(): void {
    this.httpService.getVersion().pipe(
      switchMap((version) => this.httpService.getChargingOperators(version)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (response) => {
        const list = response?.operators;
        this.operators.set((Array.isArray(list) ? list : [])
          .map((op: any) => ({ id: Number.parseInt(op?.id, 10), name: String(op?.name ?? '').trim() }))
          .filter((op: OperatorOption) => Number.isInteger(op.id) && op.id > 0 && op.name)
          .sort((a: OperatorOption, b: OperatorOption) => a.name.localeCompare(b.name)));
      },
      // No list (offline, blocked): the plain id box still works.
      error: () => this.operators.set([]),
    });
  }

  // ---------------------------------------------------------------------------

  /** Absolute origin of the running deployment, so the URL and snippets are copy-paste ready. */
  public origin(): string {
    try {
      return globalThis.location.origin;
    } catch {
      return 'https://evnavigation.com';
    }
  }

  /** The widget URL up to the `?`. The page is only routed on the widget host, whose root is the widget. */
  public widgetBase(): string {
    return `${this.origin()}/`;
  }

  public copy(id: string, text: string): void {
    navigator.clipboard?.writeText(text).then(() => {
      this.copiedId.set(id);
      setTimeout(() => {
        if (this.copiedId() === id) this.copiedId.set(null);
      }, 2000);
    }).catch(() => { });
  }
}
